import { useAdminApi } from '@hooks/useAdminApi';
import { Box, Stack } from '@mui/material';
import { TextInputSelectBox } from '@shared/components/TextFieldSelectBox';
import { useEffect, useState } from 'react';
import { StyledIconButton } from './StyledIconButton';
import { ADMIN_MANAGED_PERMISSIONS, permissionsPayload, PermissionsEnum, User } from '@protos/user';
import { BaseEditDialog } from './BaseEditDialog';
import { getCurrentUser } from '../utils';
interface EditPermissionsDialogProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: (user: User) => void;
}

export const EditPermissionsDialog: React.FC<EditPermissionsDialogProps> = ({ user, isOpen, onClose, onUserUpdated }) => {
  const { apiClient } = useAdminApi();
  const [permissions, setPermissions] = useState<string[]>(user.permissions.filter(p => ADMIN_MANAGED_PERMISSIONS.includes(p)));
  const currentUser = getCurrentUser(user);

  const onSave = async () => {
    if (apiClient && user) {
      const payload = permissionsPayload(user.permissions, permissions as PermissionsEnum[]);
      await apiClient.updateUserLimits(user.id, { ...currentUser, permissions: payload }).then(data => {
        onUserUpdated(data);
      });
    }
  };

  const onPermissionAdd = () => {
    const available = ADMIN_MANAGED_PERMISSIONS.find(p => !permissions.includes(p));
    if (available) {
      setPermissions([...permissions, available]);
    }
  };

  const onPermissionRemove = (id: number) => {
    const updatedPermissions = [...permissions];
    updatedPermissions.splice(id, 1);
    setPermissions(updatedPermissions);
  };

  const onPermissionUpdate = (id: number, value: string) => {
    const updated = [...permissions];
    updated[id] = value;
    setPermissions(updated);
  };

  useEffect(() => {
    setPermissions(user.permissions.filter(p => ADMIN_MANAGED_PERMISSIONS.includes(p)));
  }, [user, isOpen]);

  return (
    <BaseEditDialog
      title="Edit Permissions"
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[permissions]}
      defaultErrorMessage="There was an error updating the permissions"
      onSave={onSave}
    >
      <Box display="flex" marginRight={2} gap={1}>
        <Stack direction="column" alignItems="flex-start">
          {permissions.map((permission, id) => (
            <Stack key={`permission_${permission}_${id}`} direction="row">
              <TextInputSelectBox
                label="Permission"
                value={permission}
                sx={{ width: '180px' }}
                onChange={(value: string) => onPermissionUpdate(id, value)}
                options={ADMIN_MANAGED_PERMISSIONS.filter(p => !permissions.includes(p) || p === permission)}
              />
              <StyledIconButton onClick={() => onPermissionRemove(id)} size={'small'} style={{ marginLeft: '5px' }}>
                <i className={'ri-delete-bin-line'} />
              </StyledIconButton>
            </Stack>
          ))}
          <StyledIconButton onClick={onPermissionAdd} disabled={permissions.length >= ADMIN_MANAGED_PERMISSIONS.length}>
            <i className={'ri-add-line'} />
          </StyledIconButton>
        </Stack>
      </Box>
    </BaseEditDialog>
  );
};
